import React, { useMemo } from 'react';
import ChainHeader from '../ChainHeader';
import { buildTopLevelOrigins } from '../../layout/topLevelOrigins';
import './styles.css';

/**
 * One flag above every top-level mission. Left/right reorder that row.
 */
const ChainHeaders = ({
  tasks = [],
  positions = {},
  nodeDimensions = {},
  chainInfo = [],
  chains = [],
  onMoveLeft,
  onMoveRight,
  isFocusMode = false,
  isDragging = false,
  isMultiCampaignMode = false,
}) => {
  const chainMetadataMap = useMemo(() => {
    const map = new Map();
    chains.forEach(c => map.set(c.id, c));
    return map;
  }, [chains]);

  const chainInfoMap = useMemo(() => {
    const map = new Map();
    chainInfo.forEach(c => map.set(c.id, c));
    return map;
  }, [chainInfo]);

  const chainsByCampaign = useMemo(() => {
    if (!isMultiCampaignMode) return null;

    const grouped = {};
    chainInfo.forEach(chain => {
      const campaignId = chain.campaignId || 'unknown';
      if (!grouped[campaignId]) {
        grouped[campaignId] = [];
      }
      grouped[campaignId].push(chain);
    });

    Object.values(grouped).forEach(campaignChains => {
      campaignChains.sort((a, b) => (a.position?.x || 0) - (b.position?.x || 0));
    });

    return grouped;
  }, [chainInfo, isMultiCampaignMode]);

  const getChainOrder = (chainId, chain) => {
    if (isMultiCampaignMode && chainsByCampaign && chain?.campaignId) {
      const campaignChains = chainsByCampaign[chain.campaignId] || [];
      const index = campaignChains.findIndex(c => c.id === chainId);
      return index >= 0 ? index : 0;
    }

    const chainData = chainInfoMap.get(chainId);
    if (chainData?.effectiveOrder !== undefined) {
      return chainData.effectiveOrder;
    }
    const stored = chainMetadataMap.get(chainId);
    if (stored?.order !== undefined) {
      return stored.order;
    }
    const layoutIndex = chainInfo.findIndex(c => c.id === chainId);
    return layoutIndex >= 0 ? layoutIndex : 0;
  };

  const getTotalChains = (chain) => {
    if (isMultiCampaignMode && chainsByCampaign && chain?.campaignId) {
      return chainsByCampaign[chain.campaignId]?.length || 1;
    }
    return chainInfo.length;
  };

  const origins = useMemo(
    () => buildTopLevelOrigins(tasks, positions, nodeDimensions),
    [tasks, positions, nodeDimensions],
  );

  const markers = origins.length > 0 ? origins : chainInfo;

  if (isFocusMode || markers.length === 0) {
    return null;
  }

  return (
    <div className="chain-headers">
      {markers.map((chain) => {
        const fromLayout = origins.length === 0;
        const order = fromLayout ? getChainOrder(chain.id, chain) : chain.effectiveOrder;
        const totalChains = fromLayout ? getTotalChains(chain) : markers.length;

        return (
          <ChainHeader
            key={chain.id}
            chainId={chain.id}
            order={order ?? 0}
            totalChains={totalChains}
            position={chain.position}
            width={chain.width}
            onMoveLeft={isMultiCampaignMode ? null : (id) => onMoveLeft?.(id, markers)}
            onMoveRight={isMultiCampaignMode ? null : (id) => onMoveRight?.(id, markers)}
            isDragging={isDragging}
            isMultiCampaignMode={isMultiCampaignMode}
            campaignColor={chain.campaignColor}
          />
        );
      })}
    </div>
  );
};

export default ChainHeaders;
